import type { WidgetCondition, WidgetConditionCfg, WidgetType } from 'components/shared/base-product-tile/base-product-tile-types.js'

export const AVAILABLE_WIDGET_TYPES: ReadonlyArray<WidgetType> = [
  'csc-product-tile-add-to-order-template',
  'csc-brand',
  'csc-product-tile-title',
  'csc-product-tile-product-code',
  'csc-price',
  'csc-retail-price',
  'csc-product-tile-variant-indication',
  'csc-product-tile-variants',
  'csc-featured-attributes',
  'csc-stock-indication',
  'csc-product-tile-tags',
  'csc-order-form',
  'csc-plp-cta-button',
  'csc-compare-checkbox',
  'csc-quotation-add-product',
  'csc-levies',
  'csc-product-rating',
  'csc-order-template-slide-in',
] as const

// Export leaves out properties equal to DEFAULT_PROPERTIES, which assumes the component falls back
// to that same value. These keys start with a different value in the configurator than in the
// component (order template: icon-button vs. its own link; order form: a hidden unit of measure
// with the default unit vs. no unit source at all), so they're always exported.
export const ALWAYS_EXPORTED_PROPERTIES: Partial<Record<WidgetType, readonly string[]>> = {
  'csc-order-form': ['uomSource', 'hiddenFields'],
  'csc-order-template-slide-in': ['triggerType'],
}

export const DEFAULT_PROPERTIES: Record<WidgetType, Record<string, any>> = {
  'csc-product-tile-add-to-order-template': {},
  'csc-brand': { layout: 'default', fontVariant: 'caption-s' },
  'csc-product-tile-title': { fontVariant: 'product-title-m' },
  'csc-product-tile-product-code': { hiddenFields: '' },
  'csc-price': {
    hiddenFields: '',
    price1Source: 'sale',
    price1Variant: 'default',
    price1Size: 'm',
    price1Layout: 'inline',
    price1Lead: '',
    price1Uom: '',
    price1Vat: '',
    price2Source: '',
    price2Variant: 'default',
    price2Size: 's',
    price2Layout: 'inline',
    price2Lead: '',
    price2Uom: '',
    price2Vat: '',
  },
  'csc-retail-price': { fontVariant: 'body-s' },
  'csc-product-tile-variant-indication': { badgeVariant: 'info' },
  'csc-product-tile-variants': { displayLimit: 5 },
  'csc-featured-attributes': { inline: true, listVariant: 'unstyled', iconName: 'star', iconStyle: 'regular', divider: true, fontVariant: 'body-s' },
  'csc-stock-indication': { variant: 'default' },
  'csc-product-tile-tags': {},
  'csc-order-form': { layout: 'default', fit: 'fill', uomSource: 'default_uom', hiddenFields: 'uom' },
  'csc-plp-cta-button': { type: 'view-product', size: 'default', fit: 'fill' },
  'csc-compare-checkbox': { variant: 'default', priceSource: 'sale' as const },
  'csc-quotation-add-product': { variant: 'button', buttonVariant: 'default', iconVariant: 'primary', linkVariant: 'primary', fit: 'fill' },
  'csc-levies': { fontVariant: 'body-s', listVariant: 'unstyled', listIconName: 'star', listIconStyle: 'regular' },
  'csc-product-rating': {},
  'csc-order-template-slide-in': { triggerType: 'icon-button', triggerVariant: 'primary', triggerSize: 'default', triggerIconName: 'star' },
}

export const DISPLAY_NAMES: Record<WidgetType, string> = {
  'csc-product-tile-add-to-order-template': 'Add to Order Template (deprecated)',
  'csc-brand': 'Brand Name',
  'csc-product-tile-title': 'Product Title',
  'csc-product-tile-product-code': 'Product Code',
  'csc-price': 'Price',
  'csc-retail-price': 'Suggested Retail Price',
  'csc-product-tile-variant-indication': 'Variant Indication',
  'csc-product-tile-variants': 'Variants',
  'csc-featured-attributes': 'Featured Attributes',
  'csc-stock-indication': 'Stock Indication',
  'csc-product-tile-tags': 'Tags',
  'csc-order-form': 'Order Form',
  'csc-plp-cta-button': 'PDP button',
  'csc-compare-checkbox': 'Compare',
  'csc-quotation-add-product': 'Quotation: Add Product',
  'csc-levies': 'Levies',
  'csc-product-rating': 'Rating',
  'csc-order-template-slide-in': 'Add to Order Template',
}

// Visibility conditions a widget applies to itself, regardless of configuration — shown as a
// read-only "Built-in" note on the widget's Visibility tab (lowercase, it follows "Built-in:").
export const BUILT_IN_VISIBILITY: Partial<Record<WidgetType, string>> = {
  'csc-price': 'this widget is only visible if the product has a price for the selected source.',
  'csc-retail-price': 'this widget is only visible if the product has a suggested retail (consumer) price.',
  'csc-product-tile-variant-indication': 'this widget is only visible if the product has variants.',
  'csc-product-tile-variants': 'this widget is only visible if the product has variants.',
  'csc-featured-attributes': 'this widget is only visible if the product has featured attributes.',
  'csc-product-tile-tags': 'this widget is only visible if the product has badge tags.',
  'csc-levies': 'this widget is only visible if the product has levies with an amount.',
}


// Short form of BUILT_IN_VISIBILITY for the canvas badge a selected widget shows ("Built-in: has
// levies") — keep these to a few words, the badge sits on the widget's top edge.
export const BUILT_IN_VISIBILITY_SHORT: Partial<Record<WidgetType, string>> = {
  'csc-price': 'has a price',
  'csc-retail-price': 'has a retail price',
  'csc-product-tile-variant-indication': 'has variants',
  'csc-product-tile-variants': 'has variants',
  'csc-featured-attributes': 'has featured attributes',
  'csc-product-tile-tags': 'has badge tags',
  'csc-levies': 'has levies',
}


// The showWhen conditions a widget's built-in visibility already covers — not offered when adding
// a condition to that widget, since it would only repeat what the widget does by itself.
export const BUILT_IN_CONDITIONS: Partial<Record<WidgetType, WidgetCondition[]>> = {
  'csc-product-tile-variants': ['has_variants'],
  'csc-product-tile-variant-indication': ['has_variants'],
}

export const CONDITION_LABELS: Record<WidgetCondition, string> = {
  variant_grouping: 'Variant grouping',
  has_variants: 'Has variants',
  is_configurator_product: 'Configurator product',
  orderable: 'Orderable',
}

export const CONDITION_DESCRIPTIONS: Record<WidgetCondition, { is: string; isNot: string }> = {
  variant_grouping: { is: 'the product is part of a variant group', isNot: 'the product is NOT part of a variant group' },
  has_variants: { is: 'the product has variants', isNot: 'the product does NOT have variants' },
  is_configurator_product: { is: 'the product is a configurator product', isNot: 'the product is NOT a configurator product' },
  orderable: { is: 'the product can be added to the basket', isNot: 'the product can NOT be added to the basket' },
}

export const describeCondition = ({ condition, is }: WidgetConditionCfg): string =>
  `Only shown when ${is ? CONDITION_DESCRIPTIONS[condition].is : CONDITION_DESCRIPTIONS[condition].isNot}.`

const shortCondition = ({ condition, is }: WidgetConditionCfg): string =>
  `${is ? '' : 'not '}${CONDITION_LABELS[condition].toLowerCase()}`

type VisibilitySubject = { type: WidgetType; showWhen?: WidgetConditionCfg[] | undefined }

// Whether a widget's visibility depends on anything — its built-in condition and/or its own
// showWhen conditions. Drives the orange marker on canvas widgets and the Visibility tab's
// indicator dot.
export const hasVisibilityConditions = (widget: VisibilitySubject): boolean =>
  !!BUILT_IN_VISIBILITY[widget.type] || !!widget.showWhen?.length

// The label a selected widget's canvas badge shows — the built-in condition and the widget's own
// conditions, whichever apply ("Built-in: has levies · Show if variant grouping and not
// configurator product").
export const visibilityBadgeLabel = (widget: VisibilitySubject): string | null => {
  const builtIn = BUILT_IN_VISIBILITY_SHORT[widget.type]
  const conditions = widget.showWhen ?? []
  const parts = [
    ...(builtIn ? [`Built-in: ${builtIn}`] : []),
    ...(conditions.length ? [`Show if ${conditions.map(shortCondition).join(' and ')}`] : []),
  ]
  return parts.length ? parts.join(' · ') : null
}
